import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import taxService from '../../services/taxService';
import './ProductCategoryDetailPage.css';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';

const TaxDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [tax, setTax] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTax();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTax = async () => {
    setLoading(true);
    setError(null);
    try {
      const tax = await taxService.getTaxById(parseInt(id || '0'));
      if (tax) {
        setTax(tax);
      }
      setLoading(false);
    } catch (err) {
      setError(t('tax.failedToLoadTax'));
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!tax) return;
    navigate(`/products/taxes/${tax.taxId}/delete`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!tax) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{t('tax.failedToLoadTax')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/products/taxes')}
            title={t('tax.title')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('tax.title')}
          </button>
        </nav>

        <div className="detail-header">
          <h1 className="detail-title">{tax.taxName}</h1>
          <p className="detail-subtitle">{t('common.view')}</p>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('tax.taxDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('tax.taxCode')}</label>
                    <p className="detail-value detail-code">{tax.taxCode || '-'}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('tax.taxName')}</label>
                    <p className="detail-value"><strong>{tax.taxName}</strong></p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('tax.taxRate')}</label>
                    <p className="detail-value" style={{ fontSize: '18px', fontWeight: 'bold', color: '#0066cc' }}>
                      {tax.taxPercentage}%
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.status')}</label>
                    <div className="detail-value">
                      <span className="info-badge" style={{ backgroundColor: tax.isActive ? '#28a745' : '#6c757d' }}>
                        {tax.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>

                  {tax.taxType && (
                    <div className="detail-field">
                      <label className="detail-label">{t('tax.taxType')}</label>
                      <p className="detail-value">{tax.taxType}</p>
                    </div>
                  )}

                  <div className="detail-field detail-field-full">
                    <label className="detail-label">{t('tax.description')}</label>
                    <p className="detail-value detail-description">
                      {tax.description || <em className="detail-empty">{t('common.noDescription')}</em>}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('common.information')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdDate')}</label>
                    <p className="detail-value">
                      {tax.createdDate ? new Date(tax.createdDate).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US') : '-'}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdTime')}</label>
                    <p className="detail-value">
                      {tax.createdDate ? new Date(tax.createdDate).toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : 'en-US') : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() => navigate(`/products/taxes/${tax.taxId}/edit`)}
                  title={t('common.edit')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('common.edit')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  title={t('common.delete')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('common.delete')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/products/taxes')}
                  title={t('tax.title')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('tax.title')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TaxDetailPage;
